import { NgTemplateOutlet } from "@angular/common";
import { ChangeDetectionStrategy, Component, computed, inject, input, TemplateRef, viewChild } from "@angular/core";
import { toObservable, toSignal } from "@angular/core/rxjs-interop";
import { SbCreative, SbRejectedAd, State } from "@front/m19-api-client";
import { SbCreativeBrandAssets } from "@front/m19-models";
import {
  AccountSelectionService,
  AsinService,
  BrandAssetsService,
  InventoryService,
  SbRejectedAdsService,
} from "@front/m19-services";
import { IAlertComponent, IBadgeComponent, IButtonDirective, ModalService } from "@front/m19-ui";
import { Utils } from "@front/m19-utils";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { combineLatest } from "rxjs";
import { map, startWith, switchMap } from "rxjs/operators";
import { CreativeWarningDetailBlockComponent } from "./creative-warning-detail-block.component";

@Component({
  selector: "app-creative-rejected-ads",
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgTemplateOutlet,
    TranslocoDirective,
    IAlertComponent,
    IBadgeComponent,
    IButtonDirective,
    CreativeWarningDetailBlockComponent,
  ],
  templateUrl: "./creative-rejected-ads.component.html",
  host: {
    class: "block w-full border-t pt-3",
    "[hidden]": "!hasIssues()",
  },
})
export class CreativeRejectedAdsComponent {
  private readonly sbRejectedAdsService = inject(SbRejectedAdsService);
  private readonly modalService = inject(ModalService);
  private readonly translocoService = inject(TranslocoService);
  private readonly accountSelectionService = inject(AccountSelectionService);
  private readonly asinService = inject(AsinService);
  private readonly inventoryService = inject(InventoryService);
  private readonly brandAssetsService = inject(BrandAssetsService);

  readonly creative = input.required<SbCreative>();

  private readonly rejectedAdsModal = viewChild.required<TemplateRef<unknown>>("rejectedAdsModal");

  private readonly allRejectedAds = toSignal<SbRejectedAd[]>(
    this.sbRejectedAdsService.rejectedAds$.pipe(startWith([])),
  );

  readonly rejectedAds = computed(
    () => this.allRejectedAds()?.filter((ad) => ad.creativeId === this.creative().creativeId) ?? [],
  );

  readonly creativeIssues = computed(() =>
    this.rejectedAds().map((ad) => ({
      ...ad,
      rejectedByAmazon: Utils.isRejectedByAmazon(ad.status),
      amazonMessage: Utils.isAmazonMessage(ad.status),
      reasonKey: Utils.sbIssueReasonKey(ad.status, ad.description),
    })),
  );

  readonly rejectedAdCount = computed(() => this.countAds(true));
  readonly notCreatedAdCount = computed(() => this.countAds(false));
  readonly hasRejectedAds = computed(() => this.rejectedAds().length > 0);

  readonly faultyReason = toSignal(
    toObservable(this.creative).pipe(
      switchMap((creative) =>
        this.accountSelectionService.singleAccountMarketplaceSelection$.pipe(
          switchMap((am) =>
            combineLatest([
              this.asinService.getInventoryRules(am.accountId, am.marketplace),
              this.inventoryService.getSellerInventoryStats(am.accountId, am.marketplace),
              this.asinService.getCatalog(am.accountId, am.marketplace),
              this.brandAssetsService
                .getSbCreativeBrandAssets(am.accountId, am.marketplace)
                .pipe(
                  map((assets) => assets.get(creative.creativeId) ?? ({ creativeTitle: "" } as SbCreativeBrandAssets)),
                ),
            ]).pipe(
              map(([rules, stats, catalog, brandAssets]) => {
                if (!rules) return undefined;

                const { faultyCreativeReason } = Utils.getSBFaultyReasons(
                  creative,
                  brandAssets,
                  catalog,
                  rules,
                  stats,
                  this.translocoService,
                );

                if (creative.creativeAsins && creative.creativeAsins.length === 0) {
                  return "This Ad Line is missing ASINs, please edit your Ad Line to add ASINs";
                }

                return faultyCreativeReason || undefined;
              }),
            ),
          ),
        ),
      ),
    ),
  );

  readonly showFaultyReason = computed(
    () => !this.hasRejectedAds() && !!this.faultyReason() && this.creative().state === State.ON,
  );

  readonly hasIssues = computed(() => this.hasRejectedAds() || this.showFaultyReason());

  private countAds(rejectedByAmazon: boolean): number {
    return this.creativeIssues()
      .filter((ad) => ad.rejectedByAmazon === rejectedByAmazon)
      .reduce((total, ad) => total + (ad.count ?? 1), 0);
  }

  openRejectedAdsModal() {
    this.modalService.openModal(this.rejectedAdsModal(), {
      modalTitle:
        this.translocoService.translate("creative-display.ad_line_issues") + " #" + this.creative().creativeId,
      size: "md",
    });
  }
}
